/**
 * Industry catalog: the one list of Primary and Secondary Industry options.
 *
 * `value` is a STORAGE KEY, not display text. It is saved verbatim to
 * `thrive_firms.primaryIndustry` / `thrive_firms.secondaryIndustry` and the
 * server matches it exactly (`$in` filters in thrive_server/models/firmModel.ts;
 * reportModel.ts groups on the text before " - "). NEVER edit a `value` in
 * place: every firm already saved with the old string would stop matching
 * filters and reports. To change what people see, change `label` (today
 * label === value for every entry). A firm whose stored value is no longer in
 * the catalog is still shown via `withCurrentValue` as "<value> (legacy)".
 *
 * Pick-lists show the primaries sorted alphabetically with "Other" pinned
 * last, and each parent's secondaries sorted alphabetically (so the flat
 * secondary list is grouped by parent, both levels A→Z). The sort happens at
 * module load, so the literal order below does not matter — keep it sorted
 * anyway for readability. Every secondary value starts with `${parent.value} - `.
 */

export interface IndustryOption {
  readonly value: string;
  readonly label: string;
}

export interface PrimaryIndustry extends IndustryOption {
  readonly secondary: readonly IndustryOption[];
}

/** Filter sentinel meaning "any industry" (Select value, not a stored value). */
export const INDUSTRY_ANY = "all" as const;
/** Stored / Select value meaning "no industry chosen". */
export const INDUSTRY_NONE = "" as const;

const option = (value: string): IndustryOption =>
  Object.freeze({ value, label: value });

const primary = (
  value: string,
  secondary: readonly string[] = [],
): PrimaryIndustry =>
  Object.freeze({
    value,
    label: value,
    secondary: Object.freeze(secondary.map(option)),
  });

/** Pinned to the end of the primary list whatever the alphabet says. */
const PINNED_LAST = "Other";

const byLabel = (a: IndustryOption, b: IndustryOption): number =>
  a.label.localeCompare(b.label, "en", { sensitivity: "base" });

const byPrimaryOrder = (a: IndustryOption, b: IndustryOption): number =>
  Number(a.value === PINNED_LAST) - Number(b.value === PINNED_LAST) ||
  byLabel(a, b);

const RAW_CATALOG: readonly PrimaryIndustry[] = [
  primary("Accommodations - Bed and breakfast, or any other lodging facility"),
  primary("Business Services", [
    "Business Services - Computer systems design, software, programming services",
    "Business Services - Interior, industrial, graphic, and other specialized design services",
    "Business Services - Landscaping, extermination, janitorial and housekeeping services",
    "Business Services - Printing, market research, photography, advertising, public relations and related services",
  ]),
  primary(
    "Construction and Maintenance Services - Grading, excavating, other construction and maintenance services",
  ),
  primary("Engineering, Design and Development"),
  primary("Entertainment and Recreation", [
    "Entertainment and Recreation - Golf courses, skiing facilities, other recreation industries",
    "Entertainment and Recreation - Yoga studios, gyms, fitness studios, personal trainers",
  ]),
  primary("Food"),
  primary("Healthcare - chiropractors, dental, health clinics"),
  primary(
    "Maintenance & Repair - automobile repair, automobile dealership, cellular phone repair, landscaping and lawn maintenance",
  ),
  primary("Manufacturing"),
  primary(
    "Nonprofit - churches, religious centers, advocacy organizations, etc.",
  ),
  primary("Personal Services", [
    "Personal Services - Barber shops, hair and nail salons",
    "Personal Services - Childcare",
    "Personal Services - Diet, weight loss, spas, massage, other personal care services",
  ]),
  primary(
    "Professional & Other Services - taxes, consulting, publishing, marketing",
  ),
  primary("Retail", [
    "Retail - Bookstores, hardware, anything sold that is not food",
    "Retail - Clothing, shoe, sporting goods, resale and consignment stores",
    "Retail - Groceries",
    "Retail - Jewelry, art, hobby, musical instrument, pet supply and other miscellaneous retail stores",
    "Retail - Pharmacies, cosmetics, optical goods, other health and personal care stores",
  ]),
  primary("Technology"),
  primary("Transportation - limousine services, etc."),
  primary(PINNED_LAST),
];

/** The catalog in display order: primaries A→Z with "Other" last, children A→Z. */
export const INDUSTRY_CATALOG: readonly PrimaryIndustry[] = Object.freeze(
  RAW_CATALOG.map((p) =>
    Object.freeze({
      value: p.value,
      label: p.label,
      secondary: Object.freeze([...p.secondary].sort(byLabel)),
    }),
  ).sort(byPrimaryOrder),
);

/** The 16 primary industries, A→Z with "Other" last (no `secondary` field). */
export const PRIMARY_INDUSTRIES: readonly IndustryOption[] = Object.freeze(
  INDUSTRY_CATALOG.map(({ value, label }) => Object.freeze({ value, label })),
);

/** All 14 secondary industries, grouped by parent (A→Z), A→Z within a parent. */
export const SECONDARY_INDUSTRIES: readonly IndustryOption[] = Object.freeze(
  INDUSTRY_CATALOG.flatMap((p) => p.secondary),
);

const PRIMARY_VALUES: ReadonlySet<string> = new Set(
  PRIMARY_INDUSTRIES.map((o) => o.value),
);

const PARENT_OF_SECONDARY: ReadonlyMap<string, string> = new Map(
  INDUSTRY_CATALOG.flatMap((p) =>
    p.secondary.map((s): [string, string] => [s.value, p.value]),
  ),
);

/**
 * Secondary options for the chosen primaries, in CATALOG order whatever the
 * order of `primaries`. Unknown or empty input gives [].
 */
export function secondaryIndustriesFor(
  primaries: readonly string[],
): readonly IndustryOption[] {
  if (!primaries || primaries.length === 0) return [];
  const chosen = new Set(primaries);
  return INDUSTRY_CATALOG.filter((p) => chosen.has(p.value)).flatMap(
    (p) => p.secondary,
  );
}

/** Parent primary value of a secondary value, or undefined if unknown. */
export function primaryIndustryOf(secondaryValue: string): string | undefined {
  return PARENT_OF_SECONDARY.get(secondaryValue);
}

export function isKnownPrimaryIndustry(v: string | null | undefined): boolean {
  return typeof v === "string" && PRIMARY_VALUES.has(v);
}

export function isKnownSecondaryIndustry(
  v: string | null | undefined,
): boolean {
  return typeof v === "string" && PARENT_OF_SECONDARY.has(v);
}

/**
 * Short display name: text before " - ", then before ",", trimmed;
 * "Not Specified" for empty / whitespace / null. Mirrors the server's
 * industry grouping in thrive_server/models/reportModel.ts.
 */
export function shortIndustryLabel(v: string | null | undefined): string {
  if (!v || String(v).trim() === "") return "Not Specified";
  return String(v).split(" - ")[0].split(",")[0].trim();
}

/**
 * `options` plus the record's current value when the catalog no longer has
 * it, so a Select can still show what is stored. The extra entry is appended
 * once, labelled "<value> (legacy)". Returns `options` itself when `current`
 * is empty/null or already present.
 */
export function withCurrentValue(
  options: readonly IndustryOption[],
  current: string | null | undefined,
): readonly IndustryOption[] {
  if (!current || options.some((o) => o.value === current)) return options;
  return [...options, { value: current, label: `${current} (legacy)` }];
}
